import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ArrowLeft, Trash2, Edit, MoreHorizontal, MessageCircle, Heart, ArrowUp, UserRound, ChevronDown } from 'lucide-react';
import DOMPurify from 'dompurify';
import api from '../../services/api';
import PostCard from '../../components/PostCard';
import MetaTags from '../../components/MetaTags';
import UniversalToast from '../../components/UniversalToast';
import { convertMentionsToLinks, handleMentionClick } from '../../utils/mentionUtils';
import { Comment } from '../../types';
import './PostDetail.css';
import '../../universal.css';

const PostDetail: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const location = useLocation() as any;
  const navigate = useNavigate();

  // Format large numbers with K/M suffixes (truncate, don't round)
  const formatCommentCount = (count: number): string => {
    if (count >= 1000000) {
      const millions = Math.floor(count / 100000) / 10; // Truncate to 1 decimal
      return millions.toString().replace(/\.0$/, '') + 'M';
    } else if (count >= 10000) {
      const thousands = Math.floor(count / 100) / 10; // Truncate to 1 decimal
      return thousands.toString().replace(/\.0$/, '') + 'K';
    }
    return count.toString();
  };
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);

  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [replyToComment, setReplyToComment] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<string | null>(null);
  const [expandedReplies, setExpandedReplies] = useState<Record<string, boolean>>({});
  const [modalEditText, setModalEditText] = useState('');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showLoadingToast, setShowLoadingToast] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [isDeletingComment, setIsDeletingComment] = useState(false);
  const editModalTextareaRef = useRef<HTMLDivElement>(null);
  const optionsMenuRef = useRef<HTMLDivElement>(null);

  // Update empty class for reply textarea placeholder
  useEffect(() => {
    // Find the reply textarea in the DOM by looking for the active reply form
    const replyForm = document.querySelector('.reply-form');
    if (replyForm) {
      const replyTextarea = replyForm.querySelector('.reply-textarea') as HTMLDivElement;
      if (replyTextarea) {
        if (replyText.trim() === '') {
          replyTextarea.classList.add('empty');
        } else {
          replyTextarea.classList.remove('empty');
        }
      }
    }
  }, [replyText]);

  // Initialize edit modal textarea content and placeholder
  useEffect(() => {
    if (editModalTextareaRef.current && showEditModal) {
      // Set initial content when modal opens
      editModalTextareaRef.current.innerHTML = modalEditText;

      // Set empty class
      if (modalEditText.trim() === '') {
        editModalTextareaRef.current.classList.add('empty');
      } else {
        editModalTextareaRef.current.classList.remove('empty');
      }
    }
  }, [showEditModal]); // Only run when modal opens

  // Update empty class when modalEditText changes
  useEffect(() => {
    if (editModalTextareaRef.current) {
      if (modalEditText.trim() === '') {
        editModalTextareaRef.current.classList.add('empty');
      } else {
        editModalTextareaRef.current.classList.remove('empty');
      }
    }
  }, [modalEditText]);

  // Helper function to sanitize HTML using DOMPurify (allow only <strong>, <em>, <br>, <div>)
  const sanitizeCommentHtml = (html: string): string => {
    // Use DOMPurify to sanitize HTML, allowing only safe tags
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['br', 'strong', 'em', 'div'],
      ALLOWED_ATTR: []
    });
  };

  // Function to auto-resize textarea
  const handleEditableInput = (e: React.FormEvent<HTMLDivElement>, setter: (value: string) => void) => {
    const div = e.currentTarget;
    const text = div.innerText;

    // If text is empty, clear all HTML including <br> tags
    if (text.trim() === '') {
      div.innerHTML = '';
      setter('');
      div.classList.add('empty');
      div.style.height = 'auto';
      div.style.height = div.scrollHeight + 'px';
      return;
    }

    // Enforce max length
    if (text.length <= 1000) {
      // Don't sanitize during input - let the user see the content as-is
      // Sanitization will happen when they submit
      setter(div.innerHTML);
    } else {
      // Truncate content if exceeds limit
      const truncated = text.substring(0, 1000);
      div.innerText = truncated;
      setter(div.innerHTML);

      // Move cursor to end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(div);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    // Update placeholder visibility
    div.classList.remove('empty');

    // Auto-resize
    div.style.height = 'auto';
    div.style.height = div.scrollHeight + 'px';
  };

  // Function to auto-resize div on mount/update
  const autoResizeDiv = (div: HTMLDivElement) => {
    // Reset height to auto to get the correct scrollHeight
    div.style.height = 'auto';
    // Set height to scrollHeight to fit content
    div.style.height = `${div.scrollHeight}px`;
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>, setter: (value: string) => void) => {
    e.preventDefault();

    // Get HTML content from clipboard
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    // Use HTML if available, otherwise convert plain text newlines to <br>
    let contentToPaste;
    if (html) {
      // Don't sanitize during paste - let the user see the content as-is
      // Sanitization will happen when they submit
      contentToPaste = html;
    } else {
      // Convert plain text newlines to <br> tags
      contentToPaste = text.replace(/\n/g, '<br>');
    }

    // Normalize whitespace: replace multiple consecutive newlines with max 2
    contentToPaste = contentToPaste.replace(/\n{3,}/g, '\n\n');

    // Insert at cursor position
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();

      // Insert as HTML
      range.insertNode(document.createRange().createContextualFragment(contentToPaste));

      // Move cursor to end of pasted content
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);

      // Update state and check length
      const currentDiv = e.currentTarget;
      const currentText = currentDiv.innerText;
      if (currentText.length > 1000) {
        currentDiv.innerText = currentText.substring(0, 1000);
      }
      setter(currentDiv.innerHTML);
      autoResizeDiv(currentDiv);
    }
  };

  // Function to show error toast
  const showErrorToastMessage = (message: string) => {
    setErrorMessage(message);
    setShowErrorToast(true);
    setTimeout(() => setShowErrorToast(false), 4000);
  };

  // Function to show success toast
  const showSuccessToastMessage = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  // Function to show loading toast
  const showLoadingToastMessage = (message: string) => {
    setLoadingMessage(message);
    setShowLoadingToast(true);
  };

  useEffect(() => {
    if (postId) {
      fetchPost(postId);
      // Scroll to top when navigating to post detail
      window.scrollTo(0, 0);
    }
  }, [postId]);

  // Ensure scroll to top after content loads
  useEffect(() => {
    if (post && !loading) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  }, [post, loading]);

  // Save current location when component mounts for back button functionality
  useEffect(() => {
    // Check if we came from another page or if this is a direct navigation
    const hasPreviousLocation = sessionStorage.getItem('previousLocation');

    // If no previous location is set, this might be a direct navigation
    // In this case, we'll set a fallback location
    if (!hasPreviousLocation) {
      // Set fallback to feed (root path for logged-in users)
      sessionStorage.setItem('previousLocation', '/');
    }
  }, []);

  useEffect(() => {
    if (post) {
      fetchComments();
    }
  }, [post]);

  // Prevent body scroll when delete modal is open
  useEffect(() => {
    if (showDeleteModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDeleteModal]);

  // Prevent body scroll when edit modal is open
  useEffect(() => {
    if (showEditModal) {
      document.body.style.overflow = 'hidden';

      // Auto-resize textarea when modal opens
      if (editModalTextareaRef.current) {
        // Use setTimeout to ensure the modal is rendered
        setTimeout(() => {
          if (editModalTextareaRef.current) {
            autoResizeDiv(editModalTextareaRef.current);
          }
        }, 100);
      }
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showEditModal]);

  // Auto-resize textarea when modalEditText changes
  useEffect(() => {
    if (editModalTextareaRef.current && showEditModal) {
      autoResizeDiv(editModalTextareaRef.current);
    }
  }, [modalEditText, showEditModal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setShowOptionsMenu(null);
      }
    };

    if (showOptionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsMenu]);

  const fetchPost = async (id: string) => {
    setLoading(true);
    setError(null);
    setCommentsLoaded(false); // Reset comments loaded state for new post

    try {
      const response = await api.get(`/posts/${id}`);
      setPost(response.data.post);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Post not found');
      } else {
        setError('Failed to load post');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePostLiked = (postId: string, liked: boolean, likes_count: number) => {
    if (post && post.id === postId) {
      setPost({
        ...post,
        is_liked: liked,
        likes_count: likes_count
      });
    }
  };

  const handlePostDeleted = (postId: string) => {
    if (post && post.id === postId) {
      // Navigate back to feed after deletion
      navigate('/feed');
    }
  };

  const handlePostEdited = (postId: string, newContent: string) => {
    if (post && post.id === postId) {
      setPost({
        ...post,
        post_content: newContent
      });
    }
  };

  const handleBackClick = () => {

    // Always try to use browser's back functionality first
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    // Fallback to sessionStorage-based navigation
    const previousLocation = sessionStorage.getItem('previousLocation');
    // If no previous location is saved, default to root (which shows Feed for logged-in users)
    const targetPath = previousLocation || '/';


    // Clear the previous location to prevent navigation loops
    sessionStorage.removeItem('previousLocation');

    // Navigate back to the previous location
    navigate(targetPath);
  };

  const fetchComments = async () => {
    if (!postId) return;

    setCommentsLoading(true);
    try {
      const response = await api.get(`/posts/${postId}/comments`);
      setComments(response.data);
      setCommentsLoaded(true);
      // If navigated with highlightCommentId, attempt to scroll to it
      const targetCommentId = location?.state?.highlightCommentId;
      if (targetCommentId) {
        setTimeout(() => {
          const el = document.querySelector(`[data-comment-id="${targetCommentId}"]`);
          if (el) {
            (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            (el as HTMLElement).classList.add('highlight-comment');
            setTimeout(() => (el as HTMLElement).classList.remove('highlight-comment'), 2000);
          }
        }, 100);
      }
    } catch (err: any) {
      // Even on error, mark as loaded to prevent infinite skeleton
      setCommentsLoaded(true);
    } finally {
      setCommentsLoading(false);
    }
  };


  const handleReplyToComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !postId || !replyToComment) return;

    setIsSubmittingReply(true);
    showLoadingToastMessage('Posting reply...');

    try {
      const commentData = {
        post_id: postId,
        content: sanitizeCommentHtml(replyText).trim(),
        parent_comment_id: replyToComment
      };

      const response = await api.post(`/posts/${postId}/comments`, commentData);

      // Add the reply to the parent comment
      const updatedComments = comments.map(comment => {
        if (comment.id === replyToComment) {
          return {
            ...comment,
            replies: [...(comment.replies || []), response.data],
            replies_count: (comment.replies_count || 0) + 1 // Database trigger will handle this, but keep for immediate UI update
          };
        }
        return comment;
      });

      setComments(updatedComments);
      setReplyText('');
      setReplyToComment(null);
      setShowLoadingToast(false);
      showSuccessToastMessage('Reply posted successfully!');
    } catch (err: any) {
      setShowLoadingToast(false);
      showErrorToastMessage('Failed to post reply. Please try again.');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleToggleCommentLike = async (commentId: string) => {
    // Optimistic update
    const updateList = (list: Comment[]): Comment[] => list.map(c => {
      if (c.id === commentId) {
        const newLiked = !(c.is_liked ?? false);
        const newCount = Math.max(0, (c.likes_count || 0) + (newLiked ? 1 : -1));
        return { ...c, is_liked: newLiked, likes_count: newCount };
      }
      if (c.replies) {
        return { ...c, replies: updateList(c.replies) };
      }
      return c;
    });

    setComments(prev => updateList(prev));

    try {
      await api.post(`/comments/${commentId}/like`);
    } catch (err) {
      // Revert on error
      setComments(prev => updateList(prev));
      showErrorToastMessage('Failed to like comment. Please try again.');
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editText.trim()) return;

    setIsEditingComment(true);
    showLoadingToastMessage('Updating comment...');

    try {
      const response = await api.put(`/comments/${commentId}`, {
        content: editText.trim()
      });

      // Update the comment in the list
      const updateCommentInList = (commentsList: Comment[]): Comment[] => {
        return commentsList.map(comment => {
          if (comment.id === commentId) {
            // Extract replies from response.data to avoid overwriting existing replies
            const { replies, ...responseWithoutReplies } = response.data;
            return {
              ...comment,                    // Keep all existing data (including replies)
              ...responseWithoutReplies      // Only update the fields that changed (excluding replies)
            };
          }
          if (comment.replies) {
            return {
              ...comment,
              replies: updateCommentInList(comment.replies)
            };
          }
          return comment;
        });
      };

      setComments(updateCommentInList(comments));
      setEditingComment(null);
      setEditText('');
      setShowLoadingToast(false);
      showSuccessToastMessage('Comment updated successfully!');
    } catch (err: any) {
      setShowLoadingToast(false);
      showErrorToastMessage('Failed to edit comment. Please try again.');
    } finally {
      setIsEditingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    setShowDeleteModal(null);
    setShowOptionsMenu(null);
    setIsDeletingComment(true);
    showLoadingToastMessage('Deleting comment...');

    try {
      await api.delete(`/comments/${commentId}`);

      // Remove the comment from the list
      const removeCommentFromList = (commentsList: Comment[]): Comment[] => {
        return commentsList.map(comment => {
          // Check if this comment has the deleted comment as a reply
          if (comment.replies) {
            const hasDeletedReply = comment.replies.some(reply => reply.id === commentId);
            if (hasDeletedReply) {
              // Database trigger will handle reply count, but update UI immediately
              return {
                ...comment,
                replies: comment.replies.filter(reply => reply.id !== commentId),
                replies_count: Math.max(0, (comment.replies_count || 0) - 1)
              };
            }
            // Recursively check nested replies
            comment.replies = removeCommentFromList(comment.replies);
          }
          return comment;
        }).filter(comment => comment.id !== commentId); // Remove the main comment if it matches
      };

      const updatedComments = removeCommentFromList(comments);
      setComments(updatedComments);

      // Update the post's comment count (only for top-level comments)
      const deletedComment = comments.find(c => c.id === commentId);
      if (deletedComment && !deletedComment.parent_comment_id && post) {
        setPost({
          ...post,
          comments_count: Math.max(0, (post.comments_count || 0) - 1)
        });
      }

      setShowLoadingToast(false);
      showSuccessToastMessage('Comment deleted successfully!');
    } catch (err: any) {
      setShowLoadingToast(false);
      showErrorToastMessage('Failed to delete comment. Please try again.');
    } finally {
      setIsDeletingComment(false);
    }
  };

  const handleEditCommentModal = async () => {
    if (!modalEditText.trim() || !showEditModal) return;

    setShowEditModal(null);
    setModalEditText('');
    setShowOptionsMenu(null);
    setIsEditingComment(true);
    showLoadingToastMessage('Updating comment...');

    try {
      const response = await api.put(`/comments/${showEditModal}`, {
        content: modalEditText.trim()
      });

      // Update the comment in the list
      const updateCommentInList = (commentsList: Comment[]): Comment[] => {
        return commentsList.map(comment => {
          if (comment.id === showEditModal) {
            // Extract replies from response.data to avoid overwriting existing replies
            const { replies, ...responseWithoutReplies } = response.data;
            return {
              ...comment,                    // Keep all existing data (including replies)
              ...responseWithoutReplies      // Only update the fields that changed (excluding replies)
            };
          }
          if (comment.replies) {
            return {
              ...comment,
              replies: updateCommentInList(comment.replies)
            };
          }
          return comment;
        });
      };

      setComments(updateCommentInList(comments));
      setShowLoadingToast(false);
      showSuccessToastMessage('Comment updated successfully!');
    } catch (err: any) {
      setShowLoadingToast(false);
      showErrorToastMessage('Failed to edit comment. Please try again.');
    } finally {
      setIsEditingComment(false);
    }
  };

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d`;
    } else if (diffInSeconds < 31556952) {
      const weeks = Math.floor(diffInSeconds / 604800);
      return `${weeks}w`;
    } else {
      const years = Math.floor(diffInSeconds / 31556952);
      return `${years}y`;
    }
  };

  // Function to handle smart positioning of options menu
  const handleOptionsMenuClick = (commentId: string, event: React.MouseEvent) => {
    const isOpen = showOptionsMenu === commentId;

    if (isOpen) {
      setShowOptionsMenu(null);
      return;
    }

    // Set the menu to open first
    setShowOptionsMenu(commentId);

    // Use setTimeout to ensure the menu is rendered before checking position
    setTimeout(() => {
      const menuElement = document.querySelector(`[data-comment-id="${commentId}"] .comment-options-dropdown`) as HTMLElement;
      if (menuElement) {
        const rect = menuElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Check if menu would go below viewport
        if (rect.bottom > viewportHeight) {
          menuElement.classList.add('dropdown-flip-up');
        } else {
          menuElement.classList.remove('dropdown-flip-up');
        }
      }
    }, 10);
  };

  // Function to ensure reply form is visible when opened
  const handleReplyClick = (commentId: string) => {
    const isOpen = replyToComment === commentId;

    if (isOpen) {
      setReplyToComment(null);
      setReplyText('');
      return;
    }

    setReplyToComment(commentId);
    setReplyText('');

    // Scroll reply form into view after a short delay
    setTimeout(() => {
      const replyForm = document.querySelector(`[data-comment-id="${commentId}"] .reply-form`) as HTMLElement;
      if (replyForm) {
        replyForm.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // Skeleton data for post detail
  const renderPostDetailSkeleton = () => (
    <div className="postcard-container">
      <div className="postcard-header">
        <div className="postcard-user-info">
          <div className="postcard-user-avatar">
            <div className="postdetail-skeleton-avatar"></div>
          </div>
          <div className="postcard-user-details">
            <div className="postcard-user-name-row">
              <div className="postdetail-skeleton-username"></div>
            </div>
            <div className="postcard-time">
              <div className="postdetail-skeleton-time"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Info Skeleton */}
      <div className="postcard-content-info">
        <div className="postcard-content-header">
          <div className="postdetail-skeleton-cover-image"></div>
          <div className="postcard-content-details">
            <div className="postcard-title-with-type">
              <div className="postdetail-skeleton-title"></div>
            </div>
            <div className="postdetail-skeleton-author"></div>
            <div className="postdetail-skeleton-button"></div>
          </div>
        </div>
      </div>

      {/* User Content Skeleton */}
      <div className="postcard-user-content">
        <div className="postdetail-skeleton-content-line"></div>
        <div className="postdetail-skeleton-content-line short"></div>
      </div>

      {/* Actions Skeleton */}
      <div className="postcard-actions">
        <div className="postcard-left-actions">
          <div className="postdetail-skeleton-like-button"></div>
          <div className="postdetail-skeleton-comment-button"></div>
        </div>
        <div className="postdetail-skeleton-share-button"></div>
      </div>

      {/* Comments Skeleton */}
      <div className="comments-skeleton-section">
        <div className="comments-skeleton-header">
          <div className="comments-skeleton-title"></div>
        </div>

        {/* Comment skeletons */}
        {[1, 2, 3].map((index) => (
          <div key={index} className="comment-skeleton">
            <div className="comment-skeleton-avatar"></div>
            <div className="comment-skeleton-content">
              <div className="comment-skeleton-header">
                <div className="comment-skeleton-username"></div>
              </div>
              <div className="comment-skeleton-text">
                <div className="comment-skeleton-line"></div>
                <div className="comment-skeleton-line short"></div>
              </div>
              <div className="comment-skeleton-actions">
                <div className="comment-skeleton-reply-button"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading || commentsLoading || !commentsLoaded) {
    return (
      <div className="post-detail-container">
        <div className="post-detail-header">
          <button onClick={handleBackClick} className="post-detail-back-button">
            <ArrowLeft className="back-icon" />
          </button>
          <h1>Post</h1>
        </div>
        <div className="post-detail-content">
          {renderPostDetailSkeleton()}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="post-detail-container">
        <div className="post-detail-header">
          <button onClick={handleBackClick} className="post-detail-back-button">
            <ArrowLeft className="back-icon" />
          </button>
          <h1>Post</h1>
        </div>
        <div className="error-container">
          <div className="error-message">
            <h3>Oops!</h3>
            <p>{error}</p>
            <button onClick={() => navigate('/feed')} className="go-home-button">
              Go to Feed
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-detail-container">
        <div className="post-detail-header">
          <button onClick={handleBackClick} className="post-detail-back-button">
            <ArrowLeft className="back-icon" />
          </button>
          <h1>Post</h1>
        </div>
        <div className="error-container">
          <div className="error-message">
            <h3>Post not found</h3>
            <p>This post may have been deleted or doesn't exist.</p>
            <button onClick={() => navigate('/feed')} className="go-home-button">
              Go to Feed
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaTags
        title={`${post?.content_title || 'Post'} - Mindcast`}
        description={post?.post_content || "The Playground for Curious Minds"}
        canonicalUrl={`https://mindcast.app/post/${postId}`}
      />
      <div className="post-detail-container">
        <div className="post-detail-header">
          <button onClick={handleBackClick} className="post-detail-back-button">
            <ArrowLeft className="back-icon" />
          </button>
          <h1>Post</h1>
        </div>

        <div className="post-detail-content">
          <PostCard
            post={post}
            onLikeToggle={handlePostLiked}
            onDelete={handlePostDeleted}
            onEdit={handlePostEdited}
            onCommentAdded={() => {
              // Refresh comments list when a new comment is added
              fetchComments();
              // Update post comment count
              setPost((prev: any) => prev ? { ...prev, comments_count: (prev.comments_count || 0) + 1 } : null);
            }}
            isClickable={false}
          />



          {/* Comments Section */}
          <div className="comments-section">
            <h2 className="comments-section-title">Comments</h2>
            {/* Comments List */}
            <div className="comments-list">
              {comments.length === 0 ? (
                <div className="no-comments">
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              ) : (
                comments.map(comment => (
                  <div key={comment.id} className={`comment-item ${comment.replies && comment.replies.length > 0 ? 'has-replies' : ''}`} data-comment-id={comment.id}>
                    <div className="comment-content">
                      <div className="comment-user-avatar">
                        {comment.profile_picture_url ? (
                          <img
                            src={comment.profile_picture_url}
                            alt={`${comment.username}'s profile`}
                            className="comment-user-avatar-image"
                          />
                        ) : (
                          <UserRound className="comment-user-avatar-icon" />
                        )}
                      </div>
                      <div className="comment-container">
                        <div className="comment-user-info">
                          <Link to={`/profile/${comment.username}`} className="comment-username-link">
                            <span className="comment-username">@{comment.username}</span>
                          </Link>
                          <span className="comment-date">{formatCommentDate(comment.created_at)}</span>
                          {comment.updated_at && comment.updated_at !== comment.created_at && (
                            <span className="comment-edited">(edited)</span>
                          )}
                        </div>

                        {editingComment === comment.id ? (
                          <div className="comment-edit-form">
                            <div
                              contentEditable
                              suppressContentEditableWarning
                              dangerouslySetInnerHTML={{ __html: editText }}
                              onInput={(e) => handleEditableInput(e, setEditText)}
                              onPaste={(e) => handlePaste(e, setEditText)}
                              data-placeholder="Edit your comment..."
                              className="comment-edit-textarea"
                              role="textbox"
                              aria-multiline="true"
                            />
                            <div className="comment-edit-actions">
                              <button
                                onClick={() => {
                                  setEditingComment(null);
                                  setEditText('');
                                }}
                                className="cancel-edit-btn"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleEditComment(comment.id)}
                                disabled={!editText.trim() || isEditingComment}
                                className="save-edit-btn"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="comment-text" onClick={(e) => handleMentionClick(e, navigate)}>
                            <p dangerouslySetInnerHTML={{ __html: convertMentionsToLinks(comment.content) }} />
                          </div>
                        )}

                        <div className="comment-actions">
                          <button
                            onClick={() => handleToggleCommentLike(comment.id)}
                            className={`comment-like-button ${comment.is_liked ? 'comment-liked' : ''}`}
                          >
                            <div className="comment-like-icon-container">
                              {comment.is_liked ? (
                                <Heart className="comment-heart-icon comment-liked" fill="currentColor" />
                              ) : (
                                <Heart className="comment-heart-icon" />
                              )}
                            </div>
                            <span className="comment-like-count">{formatCommentCount(comment.likes_count || 0)}</span>
                          </button>
                          <button
                            onClick={() => handleReplyClick(comment.id)}
                            className="postcard-comment-button"
                          >
                            <MessageCircle className="action-icon" />
                            <span className="reply-count">{comment.replies_count || 0}</span>
                          </button>

                          {/* Show options menu only for own comments */}
                          {comment.author_id === JSON.parse(localStorage.getItem('user') || '{}').id && (
                            <div className="comment-options-menu" ref={showOptionsMenu === comment.id ? optionsMenuRef : null}>
                              <button
                                onClick={(e) => handleOptionsMenuClick(comment.id, e)}
                                className="comment-action-btn options-btn"
                              >
                                <MoreHorizontal className="action-icon" />
                              </button>

                              {showOptionsMenu === comment.id && (
                                <div className="comment-options-dropdown">
                                  <button
                                    onClick={() => {
                                      setShowEditModal(comment.id);
                                      setModalEditText(comment.content);
                                      setShowOptionsMenu(null);
                                    }}
                                    className="dropdown-item"
                                  >
                                    Edit
                                    <Edit className="dropdown-icon" />

                                  </button>
                                  <button
                                    onClick={() => {
                                      setShowDeleteModal(comment.id);
                                      setShowOptionsMenu(null);
                                    }}
                                    className="dropdown-item-delete"
                                  >
                                    Delete
                                    <Trash2 className="dropdown-icon" />

                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Reply Form */}
                    {replyToComment === comment.id && (
                      <form onSubmit={handleReplyToComment} className="reply-form">
                        <div className="reply-input-wrapper">
                          <div
                            contentEditable
                            suppressContentEditableWarning
                            onInput={(e) => handleEditableInput(e, setReplyText)}
                            onPaste={(e) => handlePaste(e, setReplyText)}
                            data-placeholder={`Reply to @${comment.username}...`}
                            className="reply-textarea"
                            role="textbox"
                            aria-multiline="true"
                          />
                          <button
                            type="submit"
                            disabled={!replyText.trim() || isSubmittingReply}
                            className="comment-submit-btn"
                            title={isSubmittingReply ? 'Posting...' : 'Post reply'}
                          >
                            <ArrowUp className="submit-icon" />
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Nested Replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="comment-replies">
                        {comment.replies.length >= 8 && !expandedReplies[comment.id] ? (
                          <button className="show-replies-button" onClick={() => setExpandedReplies(prev => ({ ...prev, [comment.id]: true }))}>
                            <span className="show-replies-avatar">
                              <ChevronDown className="show-replies-icon" />
                            </span>
                            Show replies ({comment.replies.length})
                          </button>
                        ) : (
                          comment.replies.map(reply => (
                            <div key={reply.id} className="reply-item" data-comment-id={reply.id}>
                              <div className="comment-content">
                                <div className="comment-user-avatar">
                                  {reply.profile_picture_url ? (
                                    <img
                                      src={reply.profile_picture_url}
                                      alt={`${reply.username}'s profile`}
                                      className="comment-user-avatar-image"
                                    />
                                  ) : (
                                    <UserRound className="comment-user-avatar-icon" />
                                  )}
                                </div>

                                <div className="comment-container">
                                  <div className="comment-user-info">
                                    <Link to={`/profile/${reply.username}`} className="comment-username-link">
                                      <span className="comment-username">@{reply.username}</span>
                                    </Link>
                                    <span className="comment-date">{formatCommentDate(reply.created_at)}</span>
                                    {reply.updated_at && reply.updated_at !== reply.created_at && (
                                      <span className="comment-edited">(edited)</span>
                                    )}
                                  </div>

                                  {editingComment === reply.id ? (
                                    <div className="comment-edit-form">
                                      <div
                                        contentEditable
                                        suppressContentEditableWarning
                                        dangerouslySetInnerHTML={{ __html: editText }}
                                        onInput={(e) => handleEditableInput(e, setEditText)}
                                        onPaste={(e) => handlePaste(e, setEditText)}
                                        data-placeholder="Edit your reply..."
                                        className="comment-edit-textarea"
                                        role="textbox"
                                        aria-multiline="true"
                                      />
                                      <div className="comment-edit-actions">
                                        <button
                                          onClick={() => {
                                            setEditingComment(null);
                                            setEditText('');
                                          }}
                                          className="cancel-edit-btn"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          onClick={() => handleEditComment(reply.id)}
                                          disabled={!editText.trim()}
                                          className="save-edit-btn"
                                        >
                                          Save
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="comment-text" onClick={(e) => handleMentionClick(e, navigate)}>
                                      <p dangerouslySetInnerHTML={{ __html: convertMentionsToLinks(reply.content) }} />
                                    </div>
                                  )}

                                  {/* Reply Actions */}
                                  <div className="comment-actions">
                                    <button
                                      onClick={() => handleToggleCommentLike(reply.id)}
                                      className={`comment-like-button ${reply.is_liked ? 'comment-liked' : ''}`}
                                    >
                                      <div className="comment-like-icon-container">
                                        {reply.is_liked ? (
                                          <Heart className="comment-heart-icon comment-liked" fill="currentColor" />
                                        ) : (
                                          <Heart className="comment-heart-icon" />
                                        )}
                                      </div>
                                      <span className="comment-like-count">{formatCommentCount(reply.likes_count || 0)}</span>
                                    </button>
                                    {reply.author_id === JSON.parse(localStorage.getItem('user') || '{}').id && (
                                      <div className="comment-options-menu" ref={showOptionsMenu === reply.id ? optionsMenuRef : null}>
                                        <button
                                          onClick={(e) => handleOptionsMenuClick(reply.id, e)}
                                          className="comment-action-btn options-btn"
                                        >
                                          <MoreHorizontal className="action-icon" />
                                        </button>

                                        {showOptionsMenu === reply.id && (
                                          <div className="comment-options-dropdown">
                                            <button
                                              onClick={() => {
                                                setShowEditModal(reply.id);
                                                setModalEditText(reply.content);
                                                setShowOptionsMenu(null);
                                              }}
                                              className="dropdown-item"
                                            >
                                              Edit
                                              <Edit className="dropdown-icon" />

                                            </button>
                                            <button
                                              onClick={() => {
                                                setShowDeleteModal(reply.id);
                                                setShowOptionsMenu(null);
                                              }}
                                              className="dropdown-item-delete"
                                            >
                                              Delete
                                              <Trash2 className="dropdown-icon" />

                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Custom Delete Modal */}
        {showDeleteModal && createPortal(
          <div className="comment-delete-modal-overlay">
            <div className="comment-delete-modal-content">
              <div className="comment-delete-modal-header">
                <h3>Delete Comment</h3>
              </div>
              <div className="comment-delete-modal-body">
                <p>Are you sure you want to delete this comment? This action cannot be undone.</p>
              </div>
              <div className="comment-delete-modal-actions">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="comment-delete-modal-cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteComment(showDeleteModal)}
                  disabled={isDeletingComment}
                  className="comment-delete-modal-delete-button"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Custom Edit Modal */}
        {showEditModal && createPortal(
          <div className="comment-edit-modal-overlay">
            <div className="comment-edit-modal-content">
              <div className="comment-edit-modal-header">
                <h3>Edit Comment</h3>
                <button
                  onClick={() => {
                    setShowEditModal(null);
                    setModalEditText('');
                  }}
                  className="comment-edit-modal-close-button"
                >
                  ×
                </button>
              </div>
              <div className="comment-edit-modal-body">
                <div
                  ref={editModalTextareaRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(e) => handleEditableInput(e, setModalEditText)}
                  onPaste={(e) => handlePaste(e, setModalEditText)}
                  data-placeholder="Edit your comment..."
                  className="comment-edit-textarea"
                  role="textbox"
                  aria-multiline="true"
                />
              </div>
              <div className="comment-edit-modal-actions">
                <button
                  onClick={handleEditCommentModal}
                  disabled={!modalEditText.trim() || isEditingComment}
                  className="comment-edit-modal-button comment-edit-modal-button-confirm"
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Universal Toasts */}
        <UniversalToast type="loading" message={loadingMessage} show={showLoadingToast} />
        <UniversalToast type="success" message={successMessage} show={showSuccessToast} />
        <UniversalToast type="error" message={errorMessage} show={showErrorToast} />
      </div>
    </>
  );
};

export default PostDetail;
